import type { IcaoCategory } from '../../../types/mapEngine';

/**
 * Maps ICAO aircraft type designator → ICAO aerodrome reference code category.
 * Covers the most common types at MPTO and major international hubs.
 * Category determines which SVG silhouette is rendered.
 *
 * ICAO Annex 14 wing-span reference:
 *   Cat B: 15 m < b ≤ 24 m   (ATR42/72, E170/175, CRJ series)
 *   Cat C: 24 m < b ≤ 36 m   (A320 family, B737 family, E190/195)
 *   Cat D: 36 m < b ≤ 52 m   (A330, B767, B757, A220-300)
 *   Cat E: 52 m < b ≤ 65 m   (B777 family, A350, B787, A340)
 *   Cat F: 65 m < b ≤ 80 m   (A380, B747-8)
 */
const TYPE_MAP: Record<string, IcaoCategory> = {
  // ── Cat B ──────────────────────────────────────────────────────────────
  AT43: 'B', AT44: 'B', AT45: 'B', AT72: 'B', AT73: 'B', AT75: 'B', AT76: 'B',
  E170: 'B', E75L: 'B', E75S: 'B',
  CRJ1: 'B', CRJ2: 'B', CRJ7: 'B', CRJ9: 'B', CRJX: 'B',
  DH8A: 'B', DH8B: 'B', DH8C: 'B', DH8D: 'B',
  SF34: 'B', JS41: 'B', BE20: 'B',

  // ── Cat C ──────────────────────────────────────────────────────────────
  A318: 'C', A319: 'C', A320: 'C', A321: 'C',
  A19N: 'C', A20N: 'C', A21N: 'C',
  B731: 'C', B732: 'C', B733: 'C', B734: 'C', B735: 'C',
  B736: 'C', B737: 'C', B738: 'C', B739: 'C',
  B37M: 'C', B38M: 'C', B39M: 'C',
  E190: 'C', E195: 'C', E290: 'C', E295: 'C',
  MD80: 'C', MD81: 'C', MD82: 'C', MD83: 'C', MD88: 'C', MD90: 'C',
  B712: 'C', F100: 'C',

  // ── Cat D ──────────────────────────────────────────────────────────────
  A310: 'D', A300: 'D',
  B752: 'D', B753: 'D', B762: 'D', B763: 'D',
  A221: 'D', A223: 'D',
  B722: 'D',

  // ── Cat E ──────────────────────────────────────────────────────────────
  A332: 'E', A333: 'E', A338: 'E', A339: 'E',
  A342: 'E', A343: 'E', A345: 'E', A346: 'E',
  A359: 'E', A35K: 'E',
  B772: 'E', B773: 'E', B77L: 'E', B77W: 'E', B778: 'E', B779: 'E',
  B788: 'E', B789: 'E', B78X: 'E',
  B741: 'E', B742: 'E', B743: 'E', B744: 'E',

  // ── Cat F ──────────────────────────────────────────────────────────────
  A380: 'F', A388: 'F',
  B748: 'F',
};

/** Returns the ICAO category for a given type code, defaulting to 'C'. */
export function getIcaoCategory(icaoType: string): IcaoCategory {
  return TYPE_MAP[icaoType.toUpperCase()] ?? 'C';
}

/**
 * Real-world wingspan in metres by ICAO category.
 * Used to compute pixel size of the silhouette at a given map zoom level.
 * Values are representative medians for the category.
 */
export const WINGSPAN_M: Record<IcaoCategory, number> = {
  B: 22,
  C: 34,
  D: 48,
  E: 60,
  F: 73,
};
